import { z } from "zod";

/**
 * Schema สำหรับ Sell Package
 */
export const SellPackageSchema = z
  .object({
    studentId: z.string().min(1, "ຕ້ອງເລືອກນັກຮຽນ"),
    templateId: z.string().min(1, "ຕ້ອງເລືອກແພັກເກດ"),
    startDate: z.string().min(1, "ຕ້ອງເລືອກວັນທີເລີ່ມຕົ້ນ"),
    deactivateExisting: z.boolean().optional().default(false),
    /** Rule ids จาก package_template_allowed_rules - ต้องเลือกเมื่อ template มี maxSelectableClasses */
    selectedRuleIds: z.array(z.string()).optional(),
    /** จำนวนครั้งที่จัดสรรให้แต่ละ class (ต้องมีจำนวนเท่ากับ selectedRuleIds เมื่อมี maxSelectableClasses) */
    classSessions: z
      .array(
        z.object({
          ruleId: z.string(),
          sessions: z.number().int().positive(),
        }),
      )
      .optional(),
    discountType: z.enum(["percentage", "fixed"]).optional(),
    discountValue: z.number().optional(),
    /** receipt = ໃບເກັບເງິນເຕັມຄັ້ງ; credit = ໃບເກັບເງິນ + ຊຳລະຕໍ່ທີ່ /billing/credit-invoices */
    settlementMode: z.enum(["receipt", "credit"]).optional().default("receipt"),
    /** ເມື່ອ settlementMode=credit: ຈຳນວນທີ່ຮັບຕອນຂາຍ (0 … ລາຄາສຸດທ້າຍ) */
    amountPaidNow: z.coerce.number().int().min(0).optional().default(0),
    /** ຈຳນວນຮອບຕໍ່ເນື່ອ (stacked purchase) — default 1 */
    quantity: z.coerce.number().int().min(1).max(24).optional().default(1),
    /** ເມື່ອ quantity > 1: ວັນຕໍ່ເນື່ອອັດຕະໂນມັດ */
    chainPeriods: z.boolean().optional().default(true),
  })
  .refine(
    (data) => {
      // ถ้ามี discountType ต้องมี discountValue
      if (data.discountType && !data.discountValue) return false;
      // ถ้ามี discountValue ต้องมี discountType
      if (data.discountValue && !data.discountType) return false;
      // ถ้ามี discountType และ discountValue
      if (data.discountType && data.discountValue) {
        // percentage: ต้องอยู่ระหว่าง 0-100
        if (data.discountType === "percentage") {
          return data.discountValue >= 0 && data.discountValue <= 100;
        }
        // fixed: ต้อง > 0
        if (data.discountType === "fixed") {
          return data.discountValue > 0;
        }
      }
      return true;
    },
    {
      message: "ສ່ວນຫຼຸດບໍ່ຖືກຕ້ອງ",
    },
  );

export type SellPackageDTO = z.infer<typeof SellPackageSchema>;

/**
 * Response สำหรับ Sell Package
 */
export const SellPackageResponseSchema = z.object({
  id: z.string(),
  studentId: z.string(),
  templateId: z.string(),
  totalSessions: z.number(),
  remainingSessions: z.number(),
  startDate: z.string(),
  expireDate: z.string(),
  status: z.string(),
  purchasedAt: z.string(),
  discountType: z.enum(["percentage", "fixed"]).nullable().optional(),
  discountValue: z.number().nullable().optional(),
  discountAmount: z.number().nullable().optional(),
  originalPrice: z.number().nullable().optional(),
  finalPrice: z.number().nullable().optional(),
  receiptId: z.string().optional(),
  purchaseBatchId: z.string().nullable().optional(),
  quantity: z.number().optional(),
  packages: z
    .array(
      z.object({
        id: z.string(),
        periodIndex: z.number(),
        startDate: z.string(),
        expireDate: z.string(),
        remainingSessions: z.number(),
        finalPrice: z.number().nullable(),
      }),
    )
    .optional(),
  credit: z
    .object({
      invoiceId: z.string(),
      totalAmount: z.number(),
      paidAmount: z.number(),
      remainingAmount: z.number(),
      status: z.string(),
    })
    .optional(),
});

export type SellPackageResponseDTO = z.infer<typeof SellPackageResponseSchema>;

/**
 * Schema สำหรับ Check Active Package
 */
export const CheckActivePackageResponseSchema = z.object({
  packages: z.array(
    z.object({
      id: z.string(),
      templateName: z.string(),
      remainingSessions: z.number(),
      expireDate: z.string(),
      status: z.string(),
    }),
  ),
  hasActivePackage: z.boolean(),
  activePackage: z
    .object({
      id: z.string(),
      templateName: z.string(),
      remainingSessions: z.number(),
      expireDate: z.string(),
      status: z.string(),
    })
    .nullable(),
});

export type CheckActivePackageResponseDTO = z.infer<
  typeof CheckActivePackageResponseSchema
>;
